import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { WizardStepper } from '@/components/wizards';
import { CadenceStep } from '@/sections/settings/components/forms/CadenceStep';
import {
  ScanSchedule,
  type ScanScheduleHandle,
  type ScanScheduleValue,
} from '@/sections/settings/components/forms/ScanSchedule';
import {
  anchorDayOfWeek,
  buildAnyTimeSentence,
  buildPending,
  buildSentence,
  clampDayOfMonth,
} from '@/sections/settings/components/forms/scanScheduleSentence';
import { SentenceText } from '@/sections/settings/components/forms/SentenceText';
import type { CadenceFrequency, ScanCadenceBase, WeekDay } from '@/types';
import { cn } from '@/utils/classname';

export interface ScanScheduleWizardProps {
  open: boolean;
  onClose: () => void;
  email?: string;
  /** Initial cadence frequency (from the `cadence` nested in the current `schedule` setting). */
  initialFrequency: CadenceFrequency;
  /** Initial day-of-month (1-31) from the `cadence` nested in the current
   *  `schedule` setting. `31` is the "Last day" sentinel (backend clamps to
   *  each month's last day). */
  initialDayOfMonth: number;
  /** Initial manual-bypass value from the current `schedule` setting. */
  initialManualBypass: boolean;
  /** Whether to open in "Any time" mode (no launch window). True for a fresh or
   *  cadence-only tenant; false when a window is already configured. */
  initialAnyTime: boolean;
}

/**
 * Derives the nested cadence payload from Step-1 frequency + the UTC window
 * Step 2 computed. `dayOfWeek` = start of the longest contiguous block of
 * window days (see anchorDayOfWeek) so a week-wrapping window like Fri–Sun
 * anchors on Friday and stays inside one weekly period; the backend validator
 * still sees it as one of the window days. `dayOfMonth` = Step 2's picker value
 * (`31` is the "Last day" sentinel the backend clamps to each month's true last
 * day). `anchorDate` is never set.
 */
function toCadencePayload(
  frequency: CadenceFrequency,
  utcDays: WeekDay[],
  utcStartHHMM: string,
  dayOfMonth: number
): ScanCadenceBase {
  return {
    frequency,
    dayOfWeek: anchorDayOfWeek(utcDays),
    dayOfMonth,
    time: utcStartHHMM,
  };
}

const WINDOW_MODES: ReadonlyArray<{ label: string; anyTime: boolean }> = [
  { label: 'Any time', anyTime: true },
  { label: 'Specific window', anyTime: false },
];

const SAVE_ERROR_FALLBACK =
  'Could not save the scan schedule. Please try again.';

/**
 * Pulls a human-readable message from a save failure. The API wrapper
 * (getQueryError) stores the backend/response message on `error.name` and a
 * generic "contact support" line on `error.message`; prefer the former when
 * it's a real string, else fall back to the generic copy.
 */
function saveErrorMessage(e: unknown): string {
  const name = e instanceof Error ? e.name : undefined;
  if (
    typeof name === 'string' &&
    name &&
    name !== 'Error' &&
    !name.startsWith('[object')
  ) {
    return name;
  }
  return SAVE_ERROR_FALLBACK;
}

export const ScanScheduleWizard: React.FC<ScanScheduleWizardProps> = ({
  open,
  onClose,
  email,
  initialFrequency,
  initialDayOfMonth,
  initialManualBypass,
  initialAnyTime,
}) => {
  const [step, setStep] = useState<number>(1);
  const [frequency, setFrequency] =
    useState<CadenceFrequency>(initialFrequency);
  const [dayOfMonth, setDayOfMonth] = useState<number>(
    clampDayOfMonth(initialDayOfMonth)
  );
  const [step2, setStep2] = useState<ScanScheduleValue | undefined>(undefined);
  const [step2Valid, setStep2Valid] = useState(false);
  const [manualBypass, setManualBypass] =
    useState<boolean>(initialManualBypass);
  const [anyTime, setAnyTime] = useState<boolean>(initialAnyTime);
  const [saving, setSaving] = useState(false);

  // Both own-tenant and customer-edit are now 2 steps. The manual-bypass toggle
  // lives inside Step 2 (own-tenant only, gated by ScanSchedule's `!customerEmail`),
  // so there is no longer a dedicated manual step.
  const totalSteps = 2;
  const isLastStep = step === totalSteps;

  const step2Ref = useRef<ScanScheduleHandle>(null);

  // Live summary band (C4). Any-time: window-less sentence. Otherwise the
  // pending sentence (E5) until ≥1 day chosen, then the full window sentence.
  const hasDays = (step2?.days?.length ?? 0) > 0;
  const pending = buildPending(frequency);
  const sentence = anyTime
    ? buildAnyTimeSentence(frequency, dayOfMonth)
    : hasDays && step2
      ? buildSentence({
          frequency,
          days: step2.days,
          start: step2.start ?? '',
          end: step2.end ?? '',
          dayOfMonth,
          allDay: step2.allDay,
        })
      : null;

  async function handleSaveSchedule() {
    if (saving) return;

    // Build the cadence payload. Any-time has no window to derive from, so it
    // anchors weekly/biweekly on Monday 00:00 UTC (decision a). The windowed
    // path derives dayOfWeek/time from the form's UTC values (single tz source).
    let cadence;
    if (anyTime) {
      cadence = {
        frequency,
        dayOfWeek: 1, // Monday — no window to anchor on
        dayOfMonth: clampDayOfMonth(dayOfMonth),
        time: '00:00',
      };
    } else {
      const utc = step2Ref.current?.computeUtc();
      if (!utc) {
        toast.error('Choose at least one day and a valid time window.');
        return;
      }
      cadence = toCadencePayload(frequency, utc.days, utc.start, dayOfMonth);
    }

    setSaving(true);
    try {
      // submit() performs two writes: first the cadence in the `scan-cadence`
      // Configuration (additive, Praetorian-only — a non-Praetorian 403 aborts
      // before the window is touched), then the window in the `schedule` Setting
      // (a destructive full-document replace). Cadence-first ordering means a
      // partial failure can't lose the cadence after the destructive replace.
      // A rejection from either propagates to the catch below.
      const written = await step2Ref.current?.submit(cadence);
      if (!written) {
        toast.error('Could not save the scan schedule. Please try again.');
        return;
      }

      // Cache invalidation is handled by the mutation hooks inside submit():
      // useUpdateSetting invalidates ['MY','setting',...email] and
      // useUpdateConfiguration invalidates ['MY','configuration',...email], so
      // the scan-schedule card re-reads the updated document on its own.
      toast.success('Scan schedule updated');
      onClose();
    } catch (e) {
      // Surface the backend's specific rejection (e.g. an invalid schedule) so
      // the operator can fix it, instead of a generic failure.
      toast.error(saveErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="bg-chariot-layer0 max-w-[720px] gap-0 overflow-hidden p-0"
        data-testid="scan-schedule-wizard"
        onOpenAutoFocus={e => {
          // C7: focus the first interactive control of step 1 (the radiogroup).
          e.preventDefault();
          const root = e.currentTarget as HTMLElement;
          root
            .querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')
            ?.focus();
        }}
      >
        {/* C2 — header: title, subtitle, brand-tertiary calendar tile */}
        <div className="border-chariot-stroke flex items-start gap-3 border-b p-4">
          <div className="bg-chariot-brand-tertiary text-chariot-brand-primary flex size-8 shrink-0 items-center justify-center rounded-md">
            <CalendarIcon className="size-4" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-base font-bold">
              Edit Scan Schedule
            </DialogTitle>
            <DialogDescription className="text-chariot-text-secondary mt-0.5 text-xs">
              Applied to all assets in this tenant.
            </DialogDescription>
          </div>
        </div>

        {/* C3 — step indicator (canonical WizardStepper, dots variant) */}
        <div className="border-chariot-stroke bg-chariot-layer1 border-b px-4 py-3">
          <WizardStepper
            variant="dots"
            currentStep={step}
            steps={[
              { id: 'cadence', title: 'Cadence' },
              { id: 'days-times', title: 'Days & Times' },
            ]}
            ariaLabel="Scan schedule steps"
          />
        </div>

        {/* C4 — persistent live summary band */}
        <div className="border-chariot-stroke bg-chariot-brand-tertiary border-b px-4 py-3">
          <div className="text-chariot-text-secondary mb-1 text-xs font-bold tracking-wide uppercase">
            This builds the schedule
          </div>
          <p
            className="text-chariot-text-primary text-sm"
            data-testid="scan-schedule-summary-band"
          >
            {sentence ? (
              <SentenceText parts={sentence} />
            ) : (
              <>
                <SentenceText parts={pending.lead} />
                {pending.pending && (
                  <span className="text-chariot-text-disabled font-normal italic">
                    {pending.pending}
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {/* Step bodies — single fixed height + internal scroll so both steps
            are the same size (no dialog resize between steps). */}
        <div className="h-[480px] min-w-0 overflow-y-auto p-5">
          {step === 1 && (
            <CadenceStep value={frequency} onChange={setFrequency} />
          )}
          {/* Step 2 (Days & Times) is the last step; its Save button calls
              step2Ref.current.submit(). The manual-bypass toggle now renders
              inside ScanSchedule (own-tenant only). */}
          {step === 2 && (
            <div
              role="group"
              aria-label="Step 2: Days and Times"
              data-testid="scan-schedule-wizard-step-2"
            >
              {/* Window mode: "Any time" (cadence-only) vs a launch window.
                  Accessible radiogroup with roving tabindex + arrow-key nav,
                  matching CadenceStep (WAI-ARIA APG). */}
              <div
                role="radiogroup"
                aria-label="Launch window"
                className="border-chariot-stroke mb-4 inline-flex rounded-md border p-0.5"
              >
                {WINDOW_MODES.map(mode => {
                  const checked = anyTime === mode.anyTime;
                  return (
                    <div
                      key={mode.label}
                      role="radio"
                      aria-checked={checked}
                      tabIndex={checked ? 0 : -1}
                      onClick={() => setAnyTime(mode.anyTime)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setAnyTime(mode.anyTime);
                        } else if (
                          e.key === 'ArrowRight' ||
                          e.key === 'ArrowDown'
                        ) {
                          e.preventDefault();
                          setAnyTime(!anyTime);
                        } else if (
                          e.key === 'ArrowLeft' ||
                          e.key === 'ArrowUp'
                        ) {
                          e.preventDefault();
                          setAnyTime(!anyTime);
                        }
                      }}
                      data-testid={`scan-schedule-window-mode-${mode.anyTime ? 'any-time' : 'window'}`}
                      className={cn(
                        'cursor-pointer rounded px-3 py-1 text-sm font-medium transition-colors',
                        'focus-visible:outline-chariot-brand-primary focus-visible:outline-2 focus-visible:outline-offset-2',
                        checked
                          ? 'bg-chariot-brand-primary text-white'
                          : 'text-chariot-text-secondary hover:text-chariot-text-primary'
                      )}
                    >
                      {mode.label}
                    </div>
                  );
                })}
              </div>
              <ScanSchedule
                ref={step2Ref}
                customerEmail={email}
                embedded
                anyTime={anyTime}
                frequency={frequency}
                initialFrequency={initialFrequency}
                manualBypass={manualBypass}
                onManualBypassChange={setManualBypass}
                dayOfMonth={dayOfMonth}
                onDayOfMonthChange={setDayOfMonth}
                onChange={setStep2}
                onValidityChange={setStep2Valid}
              />
            </div>
          )}
        </div>

        {/* C5 — footer chrome (single unified footer) */}
        <div className="border-chariot-stroke bg-chariot-layer0 flex items-center gap-3 border-t p-4">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="scan-schedule-wizard-cancel"
          >
            Cancel
          </Button>
          <div className="flex-1" />
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              data-testid="scan-schedule-wizard-back"
            >
              <ChevronLeftIcon className="size-3.5" />
              Back
            </Button>
          )}
          {!isLastStep && (
            <Button
              variant="default"
              onClick={() => setStep(s => Math.min(totalSteps, s + 1))}
              disabled={step === 2 && !step2Valid}
              data-testid="scan-schedule-wizard-next"
            >
              Next
              <ChevronRightIcon className="size-3.5" />
            </Button>
          )}
          {isLastStep && (
            <Button
              variant="default"
              onClick={handleSaveSchedule}
              disabled={!step2Valid || saving}
              data-testid="scan-schedule-wizard-save"
            >
              Save
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
